<template>
  <div class="saved-cards-container">
    <div class="saved-cards-header">
      <div class="saved-cards-header-row">
        <div>
          <h4 class="saved-cards-title">
            <b-icon icon="credit-card" class="mr-2"></b-icon>
            {{ $t('savedCards.title') }}
          </h4>
          <p class="saved-cards-subtitle">{{ $t('savedCards.subtitle') }}</p>
        </div>
        <b-button
          variant="primary"
          size="sm"
          @click="addCard"
          :disabled="addingCard"
        >
          <b-spinner small v-if="addingCard" class="mr-1" />
          <b-icon v-else icon="plus" class="mr-1"></b-icon>
          {{ $t('savedCards.addCard') }}
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner small />
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else-if="cards.length === 0" class="saved-cards-empty">
      <b-icon icon="credit-card" font-scale="2" class="text-muted mb-2"></b-icon>
      <p class="text-muted mb-0">{{ $t('savedCards.noCards') }}</p>
    </div>

    <div v-else class="saved-cards-list">
      <div v-for="card in cards" :key="card.uuid" class="saved-card-item">
        <div class="saved-card-info">
          <div class="saved-card-icon">
            <b-icon icon="credit-card-2-front" font-scale="1.5"></b-icon>
          </div>
          <div class="saved-card-details">
            <span class="saved-card-name">{{ card.credit_card_name }}</span>
            <span class="saved-card-number">{{ card.credit_card_number }}</span>
            <span v-if="card.expires_at" class="saved-card-expiry">
              {{ $t('savedCards.expires') }} {{ formatExpiry(card.expires_at) }}
            </span>
          </div>
        </div>
        <b-button
          variant="outline-danger"
          size="sm"
          @click="confirmDelete(card)"
          :disabled="deleting === card.uuid"
        >
          <b-spinner small v-if="deleting === card.uuid" />
          <template v-else>
            <b-icon icon="trash" class="mr-1"></b-icon>
            {{ $t('savedCards.remove') }}
          </template>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import Price from '@/plugins/protokol/lib/Price'

export default {
  name: 'SavedCards',
  data() {
    return {
      cards: [],
      loading: true,
      error: null,
      deleting: null,
      addingCard: false
    }
  },
  computed: {
    currency() {
      const { billing_address = {} } = this.$store.getters.getUser.settings || {}
      const { country } = billing_address || {}
      const p = new Price(country, this.$store.getters.getLocale)
      return p.getCurrency()
    }
  },
  methods: {
    fetchCards() {
      this.loading = true
      this.error = null
      this.$transactions.getPaymentTokens().then(resp => {
        this.cards = resp.data || []
      }).catch(() => {
        this.error = this.$t('savedCards.loadError')
      }).finally(() => {
        this.loading = false
      })
    },

    addCard() {
      this.addingCard = true
      const locale = this.$store.getters.getLocale || 'en'
      const lang = locale.split('_')[0]

      this.$transactions.saveCard({ lang, currency: this.currency }).then(resp => {
        const link = resp.data.paymentLink
        if (link) {
          window.location.href = link
        }
      }).catch(() => {
        this.$bvToast.toast(this.$t('savedCards.addError'), {
          variant: 'danger',
          title: this.$t('error'),
          solid: true
        })
      }).finally(() => {
        this.addingCard = false
      })
    },

    confirmDelete(card) {
      const name = card.credit_card_name
      const number = card.credit_card_number
      this.$mainDialog.msgBoxConfirm(
        this.$t('savedCards.confirmRemove', { card: `${name} ${number}` }),
        {
          title: this.$t('savedCards.confirmTitle'),
          okVariant: 'danger',
          okTitle: this.$t('savedCards.remove'),
          cancelTitle: this.$t('cancel')
        }
      ).then(confirmed => {
        if (confirmed) {
          this.deleteCard(card)
        }
      })
    },

    deleteCard(card) {
      this.deleting = card.uuid
      this.$transactions.deletePaymentToken(card.uuid).then(() => {
        this.cards = this.cards.filter(c => c.uuid !== card.uuid)
        this.$bvToast.toast(this.$t('savedCards.removeSuccess'), {
          variant: 'success',
          solid: true
        })
      }).catch(() => {
        this.$bvToast.toast(this.$t('savedCards.removeError'), {
          variant: 'danger',
          title: this.$t('error'),
          solid: true
        })
      }).finally(() => {
        this.deleting = null
      })
    },

    formatExpiry(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = String(d.getFullYear()).slice(-2)
      return `${month}/${year}`
    }
  },
  created() {
    this.fetchCards()
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.saved-cards-container {
  padding: 16px 0;
}

.saved-cards-header {
  margin-bottom: 24px;
}

.saved-cards-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.saved-cards-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.saved-cards-subtitle {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0;
}

.saved-cards-empty {
  text-align: center;
  padding: 32px 16px;
  background: $dark-hard2-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
}

.saved-cards-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.saved-card-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: $dark-hard2-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--text-muted);
  }
}

.saved-card-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.saved-card-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--border-default);
  border-radius: 8px;
  color: var(--text-primary);
}

.saved-card-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.saved-card-name {
  font-weight: 600;
  color: var(--text-primary);
}

.saved-card-number {
  font-family: monospace;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.saved-card-expiry {
  color: var(--text-muted);
  font-size: 0.8rem;
}
</style>
